import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { bbFetch, fmtDate, StatusPill, Title } from './bbShared';

const Row = ({ r, fields, objectifs, onReopen }) => {
  const [open, setOpen] = useState(false);
  const has = !!r.id && (!!r.date_envoi || fields.some(f => r[f.key] != null) || !!r.blocages || !!r.justification_ecart);
  return (
    <>
      <tr className={`border-t border-[#EEECE8] ${has ? 'cursor-pointer hover:bg-[#F8F7F4]' : ''}`} onClick={() => has && setOpen(o => !o)} data-testid={`bb-decl-row-${r.semaine}`}>
        <td className="py-2.5 pr-3 whitespace-nowrap font-semibold">
          <span className="inline-flex items-center gap-1">{has ? (open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : <span className="w-4" />}
            {fmtDate(r.semaine, true)} → {fmtDate(r.fin, true)}</span>
        </td>
        <td className="pr-3 whitespace-nowrap min-w-[170px]"><StatusPill status={r.statut} /></td>
        {fields.map(f => {
          const v = r[f.key]; const o = objectifs[f.key];
          return <td key={f.key} className={`pr-3 text-right font-bold ${o && v != null && v < o ? 'text-[#C2510F]' : ''}`}>{v ?? '—'}{o ? <span className="text-[10px] font-normal text-slate-400"> / {o}</span> : ''}</td>;
        })}
      </tr>
      {open && (
        <tr className="bg-[#F8F7F4]" data-testid={`bb-decl-detail-${r.semaine}`}>
          <td colSpan={fields.length + 2} className="px-4 py-3 text-sm space-y-1.5">
            {r.justification_ecart && <p><b>Justification de l'écart :</b> {r.justification_ecart}</p>}
            <p><b>Blocages :</b> {r.blocages || '—'}</p>
            <p><b>Priorités :</b> {(r.priorites || []).filter(Boolean).join(' · ') || '—'}</p>
            {r.date_envoi && <p className="text-xs text-slate-500">Envoyée le {fmtDate(r.date_envoi, true)} à {new Date(r.date_envoi).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })}</p>}
            {['rendue', 'rendue_ecart'].includes(r.statut) && <button type="button" className="text-xs font-bold underline text-[#C2510F]" onClick={(e) => { e.stopPropagation(); onReopen(r.id); }} data-testid={`bb-decl-reopen-${r.semaine}`}>Rouvrir la déclaration</button>}
          </td>
        </tr>
      )}
    </>
  );
};

export const DeclHistoryDialog = ({ commercialId, token, onClose }) => {
  const [data, setData] = useState(null);
  const load = () => bbFetch(token, `/bb/declarations/commercial/${commercialId}?weeks=12`).then(setData).catch(e => toast.error(e.message));
  useEffect(() => { setData(null); if (commercialId) load(); }, [commercialId]); // eslint-disable-line react-hooks/exhaustive-deps
  const reopen = async (id) => {
    try { await bbFetch(token, `/bb/declarations/${id}/reopen`, { method: 'POST' }); toast.success('Déclaration rouverte'); load(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={!!commercialId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[88vh] overflow-y-auto" data-testid="bb-decl-history-dialog">
        <DialogTitle asChild><Title className="text-2xl">Activité déclarée · {data?.commercial?.name || ''}</Title></DialogTitle>
        <p className="text-sm text-slate-500 -mt-2">12 dernières semaines écoulées · cliquez sur une semaine pour voir blocages et priorités.</p>
        {!data ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F26A1B]" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-[0.12em] text-slate-500 text-left align-bottom">
                <th className="pb-2 pr-3">Semaine</th><th className="pb-2 pr-3">Statut</th>
                {data.fields.map(f => <th key={f.key} className="pb-2 pr-3 text-right">{f.label}</th>)}
              </tr></thead>
              <tbody>
                {data.rows.map(r => <Row key={r.semaine} r={r} fields={data.fields} objectifs={data.objectifs} onReopen={reopen} />)}
                <tr className="border-t-2 border-[#25272B] font-bold" data-testid="bb-decl-totaux">
                  <td className="py-2.5 pr-3" colSpan={2}>Total 12 semaines</td>
                  {data.fields.map(f => <td key={f.key} className="pr-3 text-right">{data.totaux[f.key]}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
